import React, { useState } from 'react'
import moment from 'moment'
import clsx from 'clsx'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Button, LinearProgress } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import ModelListPage, { FILTER_TYPE } from 'app/components/ModelListPage'
import Select from 'app/components/reusable/Select'
import HttpHandler from 'app/reusable/HttpHandler'
import { useDismissModal, useModal } from 'app/ducks/modal/hooks'
import ActivityClassRollCall from '../gym/ActivityClassRollCall'
import { printAttendanceList } from './printAttendanceList'
import { categoryParam } from './modalityCategory'

export const ACTIVITY_CLASS_FILTERS = [
  {
    key: 'is_active',
    label: 'Situação',
    icon: 'toggle_on',
    type: FILTER_TYPE.BOOLEAN,
    trueLabel: 'Ativas',
    falseLabel: 'Inativas',
  },
  {
    key: 'modalidade',
    param: 'activity_modality',
    label: 'Modalidade',
    icon: 'sports',
    type: FILTER_TYPE.MODEL,
    multiple: true,
    djangoApp: 'activities',
    djangoModel: 'activity-modality',
  },
  {
    key: 'professor',
    param: 'teacher',
    label: 'Professor',
    icon: 'person',
    type: FILTER_TYPE.MODEL,
    multiple: true,
    djangoApp: 'users',
    djangoModel: 'user',
  },
  {
    key: 'espaco',
    param: 'physical_space',
    label: 'Espaço',
    icon: 'deck',
    type: FILTER_TYPE.MODEL,
    multiple: true,
    djangoApp: 'physical-spaces',
    djangoModel: 'physical-space',
  },
]

// Python's `weekday()` numbering, as stored in `week_days`.
const WEEK_DAY_OPTIONS = [
  'Segunda',
  'Terça',
  'Quarta',
  'Quinta',
  'Sexta',
  'Sábado',
  'Domingo',
].map((label, value) => ({ value: String(value), label }))

const PERIODS = {
  manha: { label: 'Manhã', params: { start_time__lt: '12:00' } },
  tarde: {
    label: 'Tarde',
    params: { start_time__gte: '12:00', start_time__lt: '18:00' },
  },
  noite: { label: 'Noite', params: { start_time__gte: '18:00' } },
}

/**
 * Every `ActivityClass` field the redesigned Modalidades page filters by
 * (description and legacy id go through the search). The less used ones
 * are `secondary`, behind "Mais filtros". With a `category` selected, the
 * modality filter only offers that category's modalities.
 */
export const fullActivityClassFilters = (category) => {
  const [situation, modality, teacher, space] = ACTIVITY_CLASS_FILTERS
  const modalityCategory = categoryParam(category)

  return [
    situation,
    {
      ...modality,
      ...(modalityCategory && {
        optionFilters: { category: modalityCategory },
      }),
    },
    teacher,
    space,
    {
      key: 'dia',
      label: 'Dia',
      icon: 'event',
      type: FILTER_TYPE.CHOICE,
      options: WEEK_DAY_OPTIONS,
      // `week_days` is a comma-separated list of single digits.
      toParams: (value) => ({ week_days__contains: value }),
    },
    {
      key: 'periodo',
      label: 'Horário',
      icon: 'schedule',
      type: FILTER_TYPE.CHOICE,
      options: Object.entries(PERIODS).map(([value, { label }]) => ({
        value,
        label,
      })),
      toParams: (value) => PERIODS[value].params,
    },
    {
      key: 'tipo',
      param: 'is_bookable_class',
      label: 'Tipo',
      icon: 'event_available',
      type: FILTER_TYPE.BOOLEAN,
      trueLabel: 'Agendáveis',
      falseLabel: 'Fixas',
    },
    {
      key: 'vagas',
      param: 'max_number_of_users',
      label: 'Vagas',
      icon: 'event_seat',
      type: FILTER_TYPE.NUMBER_RANGE,
      step: 1,
      secondary: true,
    },
    {
      key: 'inicio',
      param: 'start_date',
      label: 'Início',
      icon: 'play_circle',
      type: FILTER_TYPE.DATE_RANGE,
      secondary: true,
    },
    {
      key: 'termino',
      param: 'end_date',
      label: 'Término',
      icon: 'stop_circle',
      type: FILTER_TYPE.DATE_RANGE,
      secondary: true,
    },
    {
      key: 'cadastro',
      param: 'inclusion_datetime',
      label: 'Cadastro',
      icon: 'event_note',
      type: FILTER_TYPE.DATE_RANGE,
      secondary: true,
    },
    {
      key: 'acesso',
      param: 'access_type',
      label: 'Tipo de acesso',
      icon: 'vpn_key',
      type: FILTER_TYPE.MODEL,
      multiple: true,
      djangoApp: 'contracts',
      djangoModel: 'access-type',
      secondary: true,
    },
    {
      key: 'video',
      label: 'Vídeo',
      icon: 'videocam',
      type: FILTER_TYPE.BOOLEAN,
      trueLabel: 'Com vídeo',
      falseLabel: 'Sem vídeo',
      toParams: (value) =>
        value
          ? { video_link__startswith: 'http' }
          : { '!video_link__startswith': 'http' },
      secondary: true,
    },
  ]
}

const formatTime = (time) => time?.slice(0, 5)

const useStyles = makeStyles(({ palette }) => ({
  muted: { color: palette.text.secondary, fontSize: 12 },
  classCell: { minWidth: 150 },
  occupation: { minWidth: 110 },
  occupationBar: { height: 6, borderRadius: 3, marginTop: 4 },
  occupationWarning: {
    backgroundColor: palette.warning.light,
    '& .MuiLinearProgress-bar': { backgroundColor: palette.warning.main },
  },
  occupationFull: {
    backgroundColor: palette.error.light,
    '& .MuiLinearProgress-bar': { backgroundColor: palette.error.main },
  },
}))

// Share of the seats taken by the standing enrollments, as a whole percentage
// (not capped, so an over-booked class reads above 100%). `null` when the
// class has no seat limit, or the API didn't send the enrollment count.
export const occupationRate = ({
  enrolled_count: enrolled,
  max_number_of_users: seats,
}) =>
  seats > 0 && typeof enrolled === 'number'
    ? Math.round((100 * enrolled) / seats)
    : null

const OCCUPATION_WARNING_FROM = 80
const OCCUPATION_FULL_FROM = 100

// "8/12" — occupied seats out of the total.
const SeatsCell = ({ activityClass }) => {
  const { enrolled_count: enrolled, max_number_of_users: seats } = activityClass
  if (typeof enrolled !== 'number') return seats ?? '—'
  return seats > 0 ? `${enrolled}/${seats}` : `${enrolled}/∞`
}

const OccupationCell = ({ activityClass }) => {
  const classes = useStyles()
  const rate = occupationRate(activityClass)
  if (rate === null) return <span className={classes.muted}>Sem limite</span>

  return (
    <div className={classes.occupation}>
      <b>{rate}%</b>
      <LinearProgress
        variant="determinate"
        value={Math.min(rate, 100)}
        aria-label={`Ocupação de ${activityClass.description}`}
        className={clsx(classes.occupationBar, {
          [classes.occupationWarning]:
            rate >= OCCUPATION_WARNING_FROM && rate < OCCUPATION_FULL_FROM,
          [classes.occupationFull]: rate >= OCCUPATION_FULL_FROM,
        })}
      />
    </div>
  )
}

const formatWeekDays = (weekDays) =>
  weekDays
    ?.map((weekDay) =>
      moment()
        .day(weekDay + 1 || 0)
        .format('ddd')
    )
    .join(', ')

const cycleOptions = () =>
  Array(12)
    .fill(0)
    .map((_, idx) => {
      const cycle = moment()
        .add(idx - 6, 'month')
        .format('YYYYMM')
      return { label: cycle, value: cycle }
    })

// Asks for the cycle of the attendance lists to print.
const AttendanceCycleForm = ({ description, onConfirm }) => {
  const [cycle, setCycle] = useState(moment().format('YYYYMM'))

  return (
    <>
      <p className="mt-0">{description}</p>
      <Select
        fullWidth
        id="attendance-cycle"
        name="attendance-cycle"
        label="Ciclo"
        value={cycle}
        className="mb-3"
        onChange={(e) => setCycle(e.target.value)}
        options={cycleOptions()}
      />
      <Button
        fullWidth
        variant="contained"
        color="primary"
        onClick={() => onConfirm(cycle)}
      >
        Imprimir
      </Button>
    </>
  )
}

const ClassCell = ({ activityClass }) => {
  const classes = useStyles()
  return (
    <div className={classes.classCell}>
      <b className="text-primary">
        <Link
          to={`/activity-class-details/${activityClass.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          {activityClass.description}
        </Link>
      </b>
      {!!activityClass.legacy_id && (
        <div className={classes.muted}>Id legado {activityClass.legacy_id}</div>
      )}
    </div>
  )
}

const ClassMobileCell = ({ activityClass }) => {
  const classes = useStyles()
  return (
    <div>
      <b>{activityClass.description}</b>
      <div className={classes.muted}>
        {[
          activityClass.activity_modality,
          formatWeekDays(activityClass.week_days),
        ]
          .filter(Boolean)
          .join(' · ')}
      </div>
    </div>
  )
}

/**
 * The class list. `School` renders it as is (behind `new_list_page`);
 * `ModalitiesPage` passes `extendedFilters` (a filter per field, bookable
 * classes a filter away instead of always hidden) and the selected
 * `category`, which scopes the list, its modality filter and "print all".
 */
const ActivityClassListPage = ({ category, extendedFilters = false }) => {
  const showModal = useModal()
  const dismissModal = useDismissModal()
  const modalityCategory = categoryParam(category)

  const printAttendance = (selected, description) =>
    showModal({
      title: 'Imprimir chamadas',
      children: (
        <AttendanceCycleForm
          description={description}
          onConfirm={(cycle) => {
            dismissModal()
            printAttendanceList({ cycle, selected, category: modalityCategory })
          }}
        />
      ),
    })

  const openRollCall = (row) =>
    showModal({
      title: `Chamada · ${row.description}`,
      children: <ActivityClassRollCall classId={row.id} />,
    })

  // Every class matching the list's current filters and search — across all
  // pages, not just the one on screen.
  const printFiltered = async (list) => {
    const { insights, ...params } = list.requestParams
    try {
      const { data } = await HttpHandler.list('activities', 'activity-class', {
        ...params,
        page: 1,
        paginate_by: 1000,
      })
      const ids = (data?.data || []).map(({ id }) => id)
      if (!ids.length) return toast.info('Nenhuma turma para imprimir.')
      printAttendance(ids, `Chamadas de ${ids.length} turmas filtradas.`)
    } catch (e) {
      console.error('Erro ao buscar turmas filtradas.', { e })
      toast.error('Erro ao buscar registros.')
    }
  }

  const describe = (rows) =>
    rows.length === 1
      ? `Chamada da turma ${rows[0].description}.`
      : `Chamadas de ${rows.length} turmas.`

  return (
    <ModelListPage
      djangoApp="activities"
      djangoModel="activity-class"
      title="Turmas"
      countLabel={{ singular: 'turma', plural: 'turmas' }}
      orderBy="description"
      baseParams={
        extendedFilters
          ? {
              ...(modalityCategory && {
                activity_modality__category: modalityCategory,
              }),
              insights: true,
            }
          : { is_bookable_class: false, insights: true }
      }
      searchPlaceholder="Buscar turmas…"
      emptyStateText="Nenhuma turma encontrada"
      filters={
        extendedFilters
          ? fullActivityClassFilters(category)
          : ACTIVITY_CLASS_FILTERS
      }
      defaultFilters={extendedFilters ? { tipo: false } : undefined}
      headerActions={(list) => {
        // Filters other than the page's defaults, or a search, narrow the
        // list: print only what's on it, not every class.
        const isFiltered = !list.isDefaultFilters || !!list.searchText
        return [
          isFiltered
            ? {
                key: 'print-filtered',
                label: 'Imprimir chamadas das turmas filtradas',
                icon: 'print',
                onClick: () => printFiltered(list),
              }
            : {
                key: 'print-all',
                label: modalityCategory
                  ? 'Imprimir chamadas de todas as turmas da categoria'
                  : 'Imprimir chamadas de todas as turmas',
                icon: 'print',
                onClick: () =>
                  printAttendance(
                    'all',
                    modalityCategory
                      ? 'Chamadas de todas as turmas da categoria.'
                      : 'Chamadas de todas as turmas.'
                  ),
              },
          {
            key: 'report',
            label: 'Relatório de alunos das turmas',
            icon: 'content_paste_search',
            to: '/custom-report?app=activities&model=activity-class-user',
          },
        ]
      }}
      selection={{
        actions: [
          {
            key: 'print',
            label: 'Imprimir chamadas',
            icon: 'print',
            onClick: (rows) =>
              printAttendance(
                rows.map(({ id }) => id),
                describe(rows)
              ),
          },
        ],
      }}
      rowActions={(row) => [
        {
          key: 'details',
          label: 'Ver turma',
          icon: 'groups',
          to: `/activity-class-details/${row.id}`,
        },
        {
          key: 'roll-call',
          label: 'Fazer chamada',
          icon: 'fact_check',
          onClick: () => openRollCall(row),
        },
        {
          key: 'print',
          label: 'Imprimir chamada',
          icon: 'print',
          onClick: () => printAttendance([row.id], describe([row])),
        },
      ]}
      columns={[
        {
          key: 'description',
          header: 'Turma',
          mobile: 'primary',
          render: (row) => <ClassCell activityClass={row} />,
          renderMobile: (row) => <ClassMobileCell activityClass={row} />,
        },
        {
          key: 'activity_modality',
          header: 'Modalidade',
          mobile: false,
          render: (row) => row.activity_modality,
        },
        { key: 'teacher', header: 'Professor', render: (row) => row.teacher },
        {
          key: 'week_days',
          header: 'Dias',
          mobile: false,
          render: (row) => formatWeekDays(row.week_days),
        },
        {
          key: 'start_time',
          header: 'Horário',
          mobile: 'aside',
          render: (row) =>
            [formatTime(row.start_time), formatTime(row.end_time)]
              .filter(Boolean)
              .join('–'),
        },
        {
          key: 'physical_space',
          header: 'Espaço',
          render: (row) => row.physical_space,
        },
        {
          key: 'max_number_of_users',
          header: 'Vagas',
          align: 'right',
          width: 90,
          render: (row) => <SeatsCell activityClass={row} />,
        },
        {
          key: 'occupation',
          header: 'Ocupação',
          width: 140,
          render: (row) => <OccupationCell activityClass={row} />,
        },
      ]}
    />
  )
}

export default ActivityClassListPage
